/**
 * Which installed app this page runs in — the Sendly app, or one gym's own app.
 *
 * An app installed from a gym page is named after the gym: the `/gym/*` HTML
 * carries that gym's manifest and `apple-mobile-web-app-title`, whatever the
 * `gymPwa` flag says. The OS prints that name on every notification ("from Gi
 * Boulder"), so the push server needs to know which tokens belong to which
 * gym's app to avoid printing the gym a second time in the title. The token
 * write reads `installedGymAppId()`; the push side is `splitByTitle` in
 * apps/functions/pushNotify.js.
 *
 * Recorded once per app session in sessionStorage, which an installed app
 * starts empty on every launch: the launch path (a gym app starts on
 * `/gym/{slug}`), then the gym id once that gym's document has loaded.
 */
import { sessionStore } from "./safeStorage";

const SCOPE_KEY = "sendly-pwa-scope";
const SLUG_KEY = "sendly-pwa-gym-slug";
const ID_KEY = "sendly-pwa-gym-id";

function isStandaloneDisplay() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

/** Remember where this session launched. Only the first call counts. */
export function recordPwaLaunch(pathname) {
  if (sessionStore.get(SCOPE_KEY)) return;
  const m = /^\/gym\/([^/?#]+)/.exec(pathname || "");
  sessionStore.set(SCOPE_KEY, m ? "gym" : "platform");
  if (m) sessionStore.set(SLUG_KEY, decodeURIComponent(m[1]));
}

/**
 * Remember the launch gym's id once its document is loaded. A gym the user
 * moved to later in the session (another slug) is not the app's gym.
 */
export function recordPwaGym(gymMeta) {
  if (!gymMeta || !gymMeta.id || !gymMeta.slug) return;
  if (sessionStore.get(SCOPE_KEY) !== "gym") return;
  if (gymMeta.slug === sessionStore.get(SLUG_KEY)) sessionStore.set(ID_KEY, gymMeta.id);
}

/**
 * The id of the gym whose installed app this is; `null` for the Sendly app and
 * a browser tab; `undefined` while a gym app's gym has not loaded yet — the
 * caller then leaves the stored value alone instead of writing a wrong `null`.
 *
 * @returns {string|null|undefined}
 */
export function installedGymAppId() {
  if (!isStandaloneDisplay() || sessionStore.get(SCOPE_KEY) !== "gym") return null;
  return sessionStore.get(ID_KEY) || undefined;
}
